import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

export default function BatchVisibilityManager({ isDarkMode, userRole, currentUserId }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [visibilitySettings, setVisibilitySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contactsUser, setContactsUser] = useState(null);
  const [showEmptyBatches, setShowEmptyBatches] = useState(false);
  const [allBatchesRaw, setAllBatchesRaw] = useState([]);
  const [notifyBatches, setNotifyBatches] = useState([]); // šarže s aktivní notifikací
  // autoAssignConfig: { batchName: { autoAssign, selfManage, nextUser, eligibleCount } }
  const [autoAssignConfig, setAutoAssignConfig] = useState({});
  const [autoAssignRaw, setAutoAssignRaw] = useState([]); // pole pro PUT
  const [skipping, setSkipping] = useState({}); // { batchName: true/false }
  const [showQueuePanel, setShowQueuePanel] = useState(false);
  const [queueOverview, setQueueOverview] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Načti uživatele
      const usersRes = await fetch(`${API_URL}/users?userId=${localStorage.getItem('lastUserId') || ''}`);
      const usersData = await usersRes.json();
      setUsers(usersData);
      
      // Najdi uživatele "kontakty"
      const contactsUserData = usersData.find(user => 
        user.name?.toLowerCase().includes('kontakt') || 
        user.email?.toLowerCase().includes('kontakt')
      );
      
      if (!contactsUserData) {
        console.log('Uživatel "kontakty" nenalezen');
        setLoading(false);
        return;
      }
      
      setContactsUser(contactsUserData);
      
      // Načti šarže filtrované na kontakty@vyjedname.cz uživatele (bez načítání kontaktů)
      console.log('📊 Načítám názvy šarží pro kontakty uživatele...');
      const batchesRes = await fetch(`${API_URL}/batches?userId=${contactsUserData._id}`);
      const batchesData = await batchesRes.json();
      
      // Filtruj pouze šarže s kontakty (count > 0)
      const batchesWithContacts = batchesData
        .filter(batch => batch.count > 0)
        .map(batch => batch.batchName)
        .sort();
      
      console.log(`✅ Načteno ${batchesWithContacts.length} šarží kontakty uživatele (celkem ${batchesData.length} šarží)`);
      setAllBatches(batchesWithContacts);

      // Načti VŠECHNY šarže v systému (bez filtru userId) pro zobrazení prázdných
      const allBatchesRes = await fetch(`${API_URL}/batches?userId=${localStorage.getItem('lastUserId') || ''}`);
      const allBatchesData = await allBatchesRes.json();
      const allBatchNames = allBatchesData.map(batch => batch.batchName).sort();
      setAllBatchesRaw(allBatchNames);
      
      // Načti existující nastavení viditelnosti
      const visibilityRes = await fetch(`${API_URL}/batch-visibility?userId=${localStorage.getItem('lastUserId') || ''}`);
      const visibilityData = await visibilityRes.json();
      
      console.log('📋 Načtená data viditelnosti:', visibilityData);
      
      // Převeď na objekt pro snadnější práci
      const visibilityMap = {};
      visibilityData.forEach(setting => {
        // Kontrola, jestli userId je objekt nebo string
        let userId;
        if (!setting.userId) {
          console.warn('⚠️ Nastavení bez userId:', setting);
          return; // Přeskoč nastavení bez userId
        }
        
        if (typeof setting.userId === 'object' && setting.userId !== null) {
          userId = setting.userId._id || setting.userId;
        } else {
          userId = setting.userId;
        }
        
        if (userId) {
          visibilityMap[userId] = setting.visibleBatches || [];
          console.log(`✓ Načteno pro uživatele ${userId}: ${setting.visibleBatches?.length || 0} šarží`);
        }
      });
      
      console.log('📊 Výsledná mapa viditelnosti:', visibilityMap);
      setVisibilitySettings(visibilityMap);

      // Načti nastavení notifikací šarží
      try {
        const notifyRes = await fetch(`${API_URL}/batch-notify?userId=${localStorage.getItem('lastUserId') || ''}`);
        if (notifyRes.ok) {
          const notifyData = await notifyRes.json();
          setNotifyBatches(notifyData.notifyBatches || []);
        }
      } catch (e) { /* ignore */ }

      // Načti auto-assign konfig
      try {
        const autoRes = await fetch(`${API_URL}/batch-autoassign?userId=${localStorage.getItem('lastUserId') || ''}`);
        if (autoRes.ok) {
          const autoData = await autoRes.json();
          const map = {};
          (autoData.batches || []).forEach(b => { map[b.batchName] = b; });
          setAutoAssignConfig(map);
          setAutoAssignRaw(autoData.batches || []);
        }
      } catch (e) { /* ignore */ }
      
    } catch (error) {
      console.error('Chyba při načítání dat:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBatchNotify = (batchName) => {
    setNotifyBatches(prev =>
      prev.includes(batchName) ? prev.filter(b => b !== batchName) : [...prev, batchName]
    );
  };

  const toggleAutoAssign = (batchName, field) => {
    setAutoAssignConfig(prev => {
      const cur = prev[batchName] || { autoAssign: false, selfManage: false };
      return { ...prev, [batchName]: { ...cur, [field]: !cur[field] } };
    });
    setAutoAssignRaw(prev => {
      const idx = prev.findIndex(b => b.batchName === batchName);
      if (idx === -1) return [...prev, { batchName, autoAssign: false, selfManage: false, [field]: true }];
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: !copy[idx][field] };
      return copy;
    });
  };

  // Zajisti, že každá zobrazená šarže má v autoAssignRaw záznam
  const ensureAutoAssignEntry = (batchName) => {
    setAutoAssignRaw(prev => {
      if (prev.some(b => b.batchName === batchName)) return prev;
      return [...prev, { batchName, autoAssign: false, selfManage: false }];
    });
  };

  const loadQueueOverview = async () => {
    setQueueLoading(true);
    try {
      const res = await fetch(`${API_URL}/batch-autoassign/queue-overview?userId=${localStorage.getItem('lastUserId') || ''}`);
      if (res.ok) {
        const data = await res.json();
        setQueueOverview(data.batches || []);
      }
    } catch (e) {
      console.error('Chyba při načítání přehledu front:', e);
    } finally {
      setQueueLoading(false);
    }
  };

  const toggleQueuePanel = async () => {
    if (!showQueuePanel) {
      await loadQueueOverview();
    }
    setShowQueuePanel(prev => !prev);
  };

  const skipToNextUser = async (batchName) => {
    setSkipping(prev => ({ ...prev, [batchName]: true }));
    try {
      await fetch(`${API_URL}/batch-autoassign/next-user?userId=${localStorage.getItem('lastUserId') || ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchName, userId: localStorage.getItem('lastUserId') || '' }),
      });
      await loadData();
    } catch (e) {
      // tiché selhání
    } finally {
      setSkipping(prev => ({ ...prev, [batchName]: false }));
    }
  };

  // Přímo z přehledu front: přepne režim přidělování dané šarže, ihned uloží a znovu načte frontu.
  const [modeSaving, setModeSaving] = useState({}); // { batchName: true }
  const changeBatchModeLive = async (batchName, mode) => {
    setModeSaving(prev => ({ ...prev, [batchName]: true }));
    try {
      // Sestav aktuální konfiguraci a přepiš režim cílové šarže
      const source = autoAssignRaw.length ? autoAssignRaw : Object.values(autoAssignConfig);
      let found = false;
      const batches = source.map(b => {
        if (b.batchName === batchName) { found = true; return { ...b, assignmentMode: mode }; }
        return { ...b };
      });
      if (!found) batches.push({ batchName, autoAssign: true, selfManage: false, assignmentMode: mode });

      await fetch(`${API_URL}/batch-autoassign?userId=${localStorage.getItem('lastUserId') || ''}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batches }),
      });

      // Lokální stav + znovunačtení fronty i konfigu
      setAutoAssignRaw(batches);
      setAutoAssignConfig(prev => ({ ...prev, [batchName]: { ...(prev[batchName] || {}), assignmentMode: mode } }));
      await loadQueueOverview();
    } catch (e) {
      console.error('Chyba při změně režimu přidělování:', e);
      alert('Nepodařilo se změnit režim přidělování.');
    } finally {
      setModeSaving(prev => ({ ...prev, [batchName]: false }));
    }
  };

  const toggleBatchForUser = (userId, batchName) => {
    setVisibilitySettings(prev => {
      const userBatches = prev[userId] || [];
      const newBatches = userBatches.includes(batchName)
        ? userBatches.filter(b => b !== batchName)
        : [...userBatches, batchName];
      
      return {
        ...prev,
        [userId]: newBatches
      };
    });
  };

  const displayedBatches = showEmptyBatches ? allBatchesRaw : allBatches;

  const setAllBatchesForUser = (userId, enabled) => {
    setVisibilitySettings(prev => ({
      ...prev,
      [userId]: enabled ? [...displayedBatches] : []
    }));
  };

  const toggleBatchForAllUsers = (batchName, enabled) => {
    setVisibilitySettings(prev => {
      const newSettings = { ...prev };
      
      // Pro každého uživatele (kromě kontakty uživatele)
      users
        .filter(user => user._id !== contactsUser?._id)
        .forEach(user => {
          const userBatches = newSettings[user._id] || [];
          
          if (enabled) {
            // Přidat šarži, pokud ji ještě nemá
            if (!userBatches.includes(batchName)) {
              newSettings[user._id] = [...userBatches, batchName];
            }
          } else {
            // Odebrat šarži
            newSettings[user._id] = userBatches.filter(b => b !== batchName);
          }
        });
      
      return newSettings;
    });
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Uloži nastavení pro každého uživatele
      for (const [userId, visibleBatches] of Object.entries(visibilitySettings)) {
        await fetch(`${API_URL}/batch-visibility/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ visibleBatches })
        });
      }

      // Uloži nastavení notifikací šarží
      await fetch(`${API_URL}/batch-notify?userId=${localStorage.getItem('lastUserId') || ''}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notifyBatches })
      });

      // Uloži auto-assign konfig
      await fetch(`${API_URL}/batch-autoassign?userId=${localStorage.getItem('lastUserId') || ''}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batches: autoAssignRaw })
      });
      
      alert('Nastavení bylo úspěšně uloženo');
      
    } catch (error) {
      console.error('Chyba při ukládání nastavení:', error);
      alert('Chyba při ukládání nastavení');
    } finally {
      setSaving(false);
    }
  };

  if (userRole !== 'admin') {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">🚫 Přístup odepřen</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Tato stránka je dostupná pouze administrátorům.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Zpět na hlavní stránku
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-lg">Načítám data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/users')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 text-blue-700 dark:text-blue-200 rounded shadow transition font-semibold mb-6"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zpět na správu uživatelů
        </button>

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">⚙️ Přidělování kontaktů</h1>
          <div className="flex gap-2">
            <button
              onClick={toggleQueuePanel}
              className={`px-4 py-2 rounded font-semibold transition ${
                showQueuePanel
                  ? isDarkMode ? 'bg-purple-700 hover:bg-purple-800 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'
                  : isDarkMode ? 'bg-purple-900 hover:bg-purple-800 text-purple-200' : 'bg-purple-100 hover:bg-purple-200 text-purple-800'
              }`}
            >
              📊 {showQueuePanel ? 'Skrýt fronty' : 'Zobrazit fronty'}
            </button>
            <button
              onClick={saveSettings}
              disabled={saving}
              className={`px-6 py-2 rounded font-semibold transition ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : isDarkMode
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              {saving ? 'Ukládám...' : '💾 Uložit nastavení'}
            </button>
          </div>
        </div>

        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <h2 className="text-lg font-semibold mb-2">ℹ️ Informace</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Zde můžete nastavit, které šarže kontaktů uvidí jednotliví uživatelé a nastavit automatické přidělování leadů.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Kontakty uživatel:</strong> {contactsUser ? `${contactsUser.username} (${contactsUser.email})` : 'Nenalezen'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Dostupných šarží:</strong> {allBatches.length} se záznamy{allBatchesRaw.length > allBatches.length ? `, celkem ${allBatchesRaw.length} (${allBatchesRaw.length - allBatches.length} prázdných)` : ''}
          </p>
          <div className="mt-3">
            <button
              onClick={() => setShowEmptyBatches(prev => !prev)}
              className={`px-3 py-1.5 text-sm rounded font-medium transition ${
                showEmptyBatches
                  ? isDarkMode ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'
                  : isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              {showEmptyBatches ? '🙈 Skrýt prázdné šarže' : '👁️ Zobrazit i prázdné šarže'}
            </button>
          </div>
        </div>

        {/* ===== PANEL VIZUALIZACE FRONT ===== */}
        {showQueuePanel && (
          <div className={`mb-6 rounded-lg border overflow-hidden ${isDarkMode ? 'bg-gray-800 border-purple-600' : 'bg-purple-50 border-purple-300'}`}>
            <div className={`px-5 py-3 flex items-center justify-between border-b ${isDarkMode ? 'bg-purple-900/40 border-purple-700' : 'bg-purple-100 border-purple-300'}`}>
              <h2 className={`font-bold text-base ${isDarkMode ? 'text-purple-200' : 'text-purple-900'}`}>📊 Přehled front — kdo je na řadě</h2>
              <button
                onClick={loadQueueOverview}
                disabled={queueLoading}
                className={`px-3 py-1 text-xs rounded font-medium transition ${
                  queueLoading ? 'opacity-50 cursor-wait' : ''
                } ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-200 hover:bg-purple-300 text-purple-800'}`}
              >
                {queueLoading ? '⏳ Načítám…' : '🔄 Obnovit'}
              </button>
            </div>
            <div className="p-4">
              {queueLoading ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent" />
                </div>
              ) : queueOverview.length === 0 ? (
                <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Žádné šarže s aktivním automatickým přidělováním.
                </p>
              ) : (
                <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                  {queueOverview.map(batch => {
                    const totalLeads = batch.queue.reduce((s, u) => s + u.leadCount, 0);
                    const eligibleCount = batch.queue.filter(u => u.isEligible).length;
                    return (
                      <div key={batch.batchName} className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} shadow-sm`}>
                        {/* Záhlaví šarže */}
                        <div className={`px-4 py-2 flex items-center justify-between ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} border-b ${isDarkMode ? 'border-gray-500' : 'border-gray-200'}`}>
                          <span className="font-semibold text-sm truncate" title={batch.batchName}>{batch.batchName}</span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-purple-700 text-purple-200' : 'bg-purple-100 text-purple-700'}`}>
                              {eligibleCount} ve frontě
                            </span>
                          </div>
                        </div>
                        {/* Přepínač režimu přidělování */}
                        <div className={`px-4 py-2 flex items-center gap-2 border-b ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-100 bg-gray-50/50'}`}>
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Režim:</span>
                          <div className="inline-flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-500">
                            <button
                              onClick={() => changeBatchModeLive(batch.batchName, 'round-robin')}
                              disabled={!!modeSaving[batch.batchName]}
                              title="Leady jdou chronologicky po pořadí (round-robin)"
                              className={`px-2 py-1 text-xs font-semibold transition ${
                                batch.assignmentMode !== 'fewest-leads'
                                  ? 'bg-purple-600 text-white'
                                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              🔁 Po pořadí
                            </button>
                            <button
                              onClick={() => changeBatchModeLive(batch.batchName, 'fewest-leads')}
                              disabled={!!modeSaving[batch.batchName]}
                              title="Leady jdou vždy obchodníkovi s nejméně leady tento měsíc (dožene po dovolené)"
                              className={`px-2 py-1 text-xs font-semibold transition ${
                                batch.assignmentMode === 'fewest-leads'
                                  ? 'bg-amber-500 text-white'
                                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              ⚖️ Nejméně leadů
                            </button>
                          </div>
                          {modeSaving[batch.batchName] && <span className="text-xs text-gray-400">ukládám…</span>}
                        </div>
                        {/* Fronta uživatelů */}
                        {batch.queue.length === 0 ? (
                          <p className={`px-4 py-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádní obchodníci ve frontě.</p>
                        ) : (
                          <ul className="divide-y divide-opacity-30 divide-gray-400">
                            {batch.queue.map((user, i) => (
                              <li key={user.userId} className={`px-4 py-2.5 flex items-center gap-3 ${
                                user.isNext
                                  ? isDarkMode ? 'bg-green-900/40' : 'bg-green-50'
                                  : !user.isEligible
                                    ? isDarkMode ? 'opacity-50' : 'opacity-40'
                                    : ''
                              }`}>
                                {/* Pořadí / indikátor */}
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                  user.isNext
                                    ? 'bg-green-500 text-white'
                                    : user.isEligible
                                      ? isDarkMode ? 'bg-purple-600 text-white' : 'bg-purple-200 text-purple-800'
                                      : isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-400'
                                }`}>{user.isEligible ? user.position : '–'}</span>
                                {/* Jméno */}
                                <span className={`flex-1 text-sm font-medium truncate ${
                                  user.isNext
                                    ? isDarkMode ? 'text-green-300' : 'text-green-800'
                                    : user.isEligible
                                      ? isDarkMode ? 'text-gray-200' : 'text-gray-800'
                                      : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  {user.isNext && <span className="mr-1">➡️</span>}
                                  {user.name}
                                  {!user.isEligible && <span className="ml-1 text-xs">(mimo frontu)</span>}
                                </span>
                                {/* Počet leadů */}
                                <div className="text-right flex-shrink-0">
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                    user.leadCount > 0
                                      ? isDarkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-700'
                                      : isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {user.leadCount} {user.leadCount === 1 ? 'lead' : user.leadCount >= 2 && user.leadCount <= 4 ? 'leady' : 'leadů'} / měsíc
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {/* ===== KONEC PANELU FRONT ===== */}

        {!contactsUser ? (
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
            <div className="text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold mb-2">Uživatel "kontakty" nenalezen</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Pro správu viditelnosti šarží je potřeba mít v systému uživatele s názvem nebo emailem obsahujícím slovo "kontakt".
              </p>
            </div>
          </div>
        ) : displayedBatches.length === 0 ? (
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
            <div className="text-center">
              <div className="text-6xl mb-4">📭</div>
              <h3 className="text-xl font-semibold mb-2">Žádné šarže nenalezeny</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Uživatel "kontakty" nemá v systému žádné kontakty se šaržemi.
              </p>
            </div>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Načítám správu viditelnosti šarží...
              </p>
            </div>
          </div>
        ) : (
          <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`sticky left-0 z-20 w-40 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      Uživatel
                    </th>
                    <th className={`sticky left-40 z-20 w-36 px-6 py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border-r ${isDarkMode ? 'border-gray-500' : 'border-gray-200'}`}>
                      Akce
                    </th>
                    {displayedBatches.map(batchName => {
                      // Zjisti, kolik uživatelů má tuto šarži
                      const usersWithBatch = users
                        .filter(user => user._id !== contactsUser?._id)
                        .filter(user => (visibilitySettings[user._id] || []).includes(batchName))
                        .length;
                      const totalUsers = users.filter(user => user._id !== contactsUser?._id).length;
                      const allHaveBatch = usersWithBatch === totalUsers && totalUsers > 0;
                      const noneHaveBatch = usersWithBatch === 0;
                      const notifyOn = notifyBatches.includes(batchName);
                      const aaCfg = autoAssignConfig[batchName] || {};
                      const autoOn = !!aaCfg.autoAssign;
                      const selfManageOn = !!aaCfg.selfManage;
                      const nextUser = aaCfg.nextUser;
                      const eligibleCount = aaCfg.eligibleCount || 0;
                      const fewestMode = aaCfg.assignmentMode === 'fewest-leads';
                      
                      return (
                        <th key={batchName} className="px-3 py-3 text-center" onMouseEnter={() => ensureAutoAssignEntry(batchName)}>
                          <div className="flex flex-col gap-1 items-center">
                            <div className="text-xs font-medium uppercase tracking-wider" title={batchName}>
                              {batchName}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {usersWithBatch}/{totalUsers}
                            </div>
                            {/* Notifikace toggle */}
                            <button
                              onClick={() => toggleBatchNotify(batchName)}
                              title={notifyOn ? 'Notifikace ZAP — klikni pro vypnutí' : 'Notifikace VYP — klikni pro zapnutí'}
                              className={`text-base transition-transform hover:scale-110 ${notifyOn ? 'opacity-100' : 'opacity-30 grayscale'}`}
                            >
                              {notifyOn ? '🔔' : '🔕'}
                            </button>
                            {/* Auto-assign toggle */}
                            <button
                              onClick={() => toggleAutoAssign(batchName, 'autoAssign')}
                              title={autoOn ? 'Auto-přidělování ZAP — klikni pro vypnutí' : 'Auto-přidělování VYP — klikni pro zapnutí'}
                              className={`text-base transition-transform hover:scale-110 ${autoOn ? 'opacity-100' : 'opacity-30 grayscale'}`}
                            >
                              {autoOn ? '🤖' : '⚙️'}
                            </button>
                            {/* SelfManage toggle */}
                            <button
                              onClick={() => toggleAutoAssign(batchName, 'selfManage')}
                              title={selfManageOn ? 'Obchodníci si mohou sami zapínat — ZAP' : 'Obchodníci si mohou sami zapínat — VYP'}
                              className={`text-base transition-transform hover:scale-110 ${selfManageOn ? 'opacity-100' : 'opacity-30 grayscale'}`}
                            >
                              {selfManageOn ? '👤' : '🔒'}
                            </button>
                            {/* Kdo dostane příští lead */}
                            {autoOn && (
                              <div className="mt-1 text-[10px] text-center leading-tight">
                                {nextUser ? (
                                  <span className="text-blue-600 dark:text-blue-300 font-semibold" title={`Příští lead jde: ${nextUser.name}`}>➡️ {nextUser.name}</span>
                                ) : eligibleCount === 0 ? (
                                  <span className="text-red-500">bez ob.</span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </div>
                            )}
                            {autoOn && !fewestMode && (
                              <button
                                onClick={() => skipToNextUser(batchName)}
                                disabled={!!skipping[batchName]}
                                title="Přeskočit na dalšího uživatele ve frontě"
                                className={`mt-1 px-1.5 py-0.5 text-[10px] rounded font-semibold transition ${
                                  skipping[batchName]
                                    ? 'bg-gray-400 cursor-wait text-white'
                                    : isDarkMode
                                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                                }`}
                              >
                                {skipping[batchName] ? '…' : '▶ Další uživatel'}
                              </button>
                            )}
                            <div className="flex gap-1">
                              <button
                                onClick={() => toggleBatchForAllUsers(batchName, true)}
                                disabled={allHaveBatch}
                                className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                                  allHaveBatch
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : isDarkMode
                                      ? 'bg-green-600 hover:bg-green-700 text-white'
                                      : 'bg-green-500 hover:bg-green-600 text-white'
                                }`}
                                title="Zaškrtnout u všech"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => toggleBatchForAllUsers(batchName, false)}
                                disabled={noneHaveBatch}
                                className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                                  noneHaveBatch
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : isDarkMode
                                      ? 'bg-red-600 hover:bg-red-700 text-white'
                                      : 'bg-red-500 hover:bg-red-600 text-white'
                                }`}
                                title="Odškrtnout u všech"
                              >
                                ✗
                              </button>
                            </div>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                  {users
                    .filter(user => user._id !== contactsUser._id) // Vyloučíme uživatele "kontakty"
                    .map(user => {
                      const userBatches = visibilitySettings[user._id] || [];
                      const allSelected = displayedBatches.length > 0 && displayedBatches.every(batch => userBatches.includes(batch));
                      const noneSelected = userBatches.length === 0;
                      
                      const rowBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
                      return (
                        <tr key={user._id} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className={`sticky left-0 z-10 w-40 px-6 py-4 ${rowBg}`}>
                            <div className="text-sm font-medium">
                              {user.username}
                            </div>
                          </td>
                          <td className={`sticky left-40 z-10 w-36 px-6 py-4 text-center ${rowBg} border-r ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => setAllBatchesForUser(user._id, true)}
                                disabled={allSelected}
                                className={`px-2 py-1 text-xs rounded font-medium ${
                                  allSelected
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : isDarkMode
                                      ? 'bg-green-600 hover:bg-green-700 text-white'
                                      : 'bg-green-500 hover:bg-green-600 text-white'
                                }`}
                              >
                                Vše
                              </button>
                              <button
                                onClick={() => setAllBatchesForUser(user._id, false)}
                                disabled={noneSelected}
                                className={`px-2 py-1 text-xs rounded font-medium ${
                                  noneSelected
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : isDarkMode
                                      ? 'bg-red-600 hover:bg-red-700 text-white'
                                      : 'bg-red-500 hover:bg-red-600 text-white'
                                }`}
                              >
                                Nic
                              </button>
                            </div>
                          </td>
                          {displayedBatches.map(batchName => {
                            const isVisible = userBatches.includes(batchName);
                            return (
                              <td key={batchName} className="px-3 py-4 text-center">
                                <label className="inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isVisible}
                                    onChange={() => toggleBatchForUser(user._id, batchName)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </label>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {users.length === 0 && (
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
            <div className="text-center">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-xl font-semibold mb-2">Žádní uživatelé</h3>
              <p className="text-gray-600 dark:text-gray-400">
                V systému nejsou zaregistrováni žádní uživatelé.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
